import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import SidebarProfile from "./Sidebar/SidebarProfile";
import SidebarNavItem from "./Sidebar/SidebarNavItem";
import SidebarLanguageSwitch from "./Sidebar/SidebarLanguageSwitch";
import KorresLogo from "../Common/KorresLogo";
import { AuthContext } from "../../context";
import { LANGUAGE_STORAGE_KEY, normalizeLanguageCode, toUiLanguageCode } from "../../i18n";
import { getMessageSessions } from "../../api/postgre/chatApi";

const Sidebar = ({ activeLabel = "Home", isMobileOpen = false, onClose }) => {
    const { logout, user } = useContext(AuthContext);
    const { i18n, t } = useTranslation();
    const [language, setLanguage] = useState(() =>
        toUiLanguageCode(
            localStorage.getItem(LANGUAGE_STORAGE_KEY) || i18n.language || i18n.resolvedLanguage
        )
    );
    const [messagesBadge, setMessagesBadge] = useState(0);
    const fullName = [user?.firstName || user?.FirstName || "", user?.lastName || user?.LastName || ""]
        .join(" ")
        .trim();
    const fallbackName = user?.username || user?.Username || t("SIDEBAR_CREATOR_FALLBACK");
    const displayName = fullName || fallbackName;
    const displayEmail = user?.email || user?.Email || t("SIDEBAR_NO_EMAIL");
    const displayInitial = String(displayName).trim().charAt(0).toUpperCase() || "K";
    const displayPhoto = user?.profilePhotoUrl || "";

    const handleSignOut = () => {
        if (onClose) {
            onClose();
        }
        logout();
    };

    useEffect(() => {
        setLanguage(toUiLanguageCode(i18n.language || i18n.resolvedLanguage));
    }, [i18n.language, i18n.resolvedLanguage]);

    useEffect(() => {
        let mounted = true;

        const loadMessageBadge = async () => {
            try {
                const sessions = await getMessageSessions();
                if (!mounted) return;
                const totalUnread = (sessions || []).reduce(
                    (total, item) => total + (Number(item.unreadCount) || 0),
                    0
                );
                setMessagesBadge(totalUnread);
            } catch {
                // silently fail on poll
            }
        };

        loadMessageBadge();
        const interval = setInterval(loadMessageBadge, 15000);

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    const handleLanguageChange = (nextLanguage) => {
        const normalizedLanguage = normalizeLanguageCode(nextLanguage);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
        setLanguage(toUiLanguageCode(normalizedLanguage));
        i18n.changeLanguage(normalizedLanguage);
    };

    const navItems = [
        { key: "SIDEBAR_HOME", label: "Home", to: "/dashboard" },
        { key: "SIDEBAR_ACCOUNT", label: "Account", to: "/my-account" },
        { key: "SIDEBAR_MESSAGES", label: "Messages", to: "/messages", badge: messagesBadge },
        { key: "SIDEBAR_MY_CONTENT", label: "My content", to: "/my-content" },
        { key: "SIDEBAR_PRODUCT_COLLECTIONS", label: "Product collections", to: "/product-collections" },
        { key: "SIDEBAR_MY_GIFTS", label: "My gifts", to: "/my-gifts" },
        { key: "SIDEBAR_GIFT_ORDERS", label: "Gift orders", to: "/gift-orders" },
        // { key: "SIDEBAR_NOTIFICATIONS", label: "Notifications", to: "/notifications", badge: 3 },
        { key: "SIDEBAR_HELP", label: "Help", to: "/help" },
    ];
    // Aura hidden temporarily — see the two commented-out {auraLink(...)} call
    // sites below. Uncomment this whole block plus those to restore it.
    // const isAuraActive = pathname.startsWith("/aura-ai-assistant");
    //
    // const auraLink = (handleClick) => (
    //     <Link
    //         to="/aura-ai-assistant"
    //         onClick={handleClick}
    //         className={`group relative flex min-h-[72px] shrink-0 items-center gap-3 overflow-hidden rounded-2xl border px-3 py-2.5 transition-all focus:outline-none ${
    //             isAuraActive
    //                 ? "border-violet-400/60 bg-[#100b28] shadow-[0_12px_28px_rgba(77,45,150,0.22)]"
    //                 : "border-violet-300/40 bg-gradient-to-r from-[#0c091c] to-[#17102f] shadow-[0_10px_24px_rgba(77,45,150,0.16)] hover:-translate-y-0.5 hover:border-violet-400/70 hover:shadow-[0_14px_30px_rgba(77,45,150,0.24)]"
    //         }`}
    //     >
    //         <span className="absolute -right-6 -top-8 h-24 w-24 rounded-full bg-violet-500/20 blur-2xl transition group-hover:bg-violet-400/30" />
    //         <span className="relative flex h-12 w-12 shrink-0 items-center justify-center">
    //             <img
    //                 src="/assets/images/aura/aura-icon.png"
    //                 alt=""
    //                 className="h-12 w-12 object-contain drop-shadow-[0_0_8px_rgba(192,132,252,0.55)]"
    //             />
    //         </span>
    //         <span className="relative min-w-0 flex-1">
    //             <span className="flex items-center gap-2">
    //                 <span className="text-sm font-semibold tracking-[0.08em] text-white">AURA</span>
    //                 <span className="rounded-full border border-violet-300/30 bg-violet-400/15 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-violet-200">AI</span>
    //             </span>
    //             <span className="mt-1 block text-[10px] leading-tight text-violet-200/75">
    //                 {t("SIDEBAR_AURA_SUBTITLE", "Your creator assistant")}
    //             </span>
    //         </span>
    //         <ArrowRight className="relative h-4 w-4 shrink-0 text-violet-300 transition-transform group-hover:translate-x-0.5" />
    //     </Link>
    // );

    return (
        <>
            <aside className="fixed space-y-5 left-0 top-0 hidden h-screen w-72 flex-col overflow-y-auto scrollbar-thin border-r border-slate-200 bg-sidebar p-6 md:flex">
                <Link to="/dashboard" className="hover:opacity-75 transition-opacity px-4 mb-2">
                    <KorresLogo />
                </Link>
                <SidebarProfile
                    name={displayName}
                    email={displayEmail}
                    initial={displayInitial}
                    photoUrl={displayPhoto}
                />
                <hr className="border-slate-200 -mx-6" />
                {/* Aura hidden temporarily — /aura-ai-assistant route still works directly */}
                {/* {auraLink()} */}
                <nav className="space-y-1 text-[12px]">
                    {navItems.map((item) => (
                        <SidebarNavItem
                            key={item.key}
                            to={item.to}
                            label={t(item.key, item.label)}
                            activeLabel={activeLabel}
                            badge={item.badge}
                        />
                    ))}
                    <button
                        type="button"
                        onClick={handleSignOut}
                        className="px-4 py-3 text-left text-sm font-semibold text-rose-400 focus:outline-none"
                    >
                        {t("SIDEBAR_SIGN_OUT")}
                    </button>
                </nav>
                <div className="flex-1" />
                <SidebarLanguageSwitch value={language} onChange={handleLanguageChange} />
            </aside>
            {isMobileOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute inset-0 bg-black/40 focus:outline-none"
                        aria-label="Close menu"
                    />
                    <aside className="relative z-10 flex h-full w-80 max-w-[85%] flex-col overflow-y-auto scrollbar-thin bg-sidebar shadow-xl">
                        <div className="-mx-0 flex items-center gap-3 border-b border-slate-200 bg-[#f4f4f5] px-4 py-4 shadow-sm">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex h-7 w-7 items-center justify-center text-slate-800 focus:outline-none"
                                aria-label="Close menu"
                            >
                                <X className="h-5 w-5" />
                            </button>
                            <Link to="/dashboard" onClick={onClose} className="hover:opacity-75 transition-opacity">
                                <KorresLogo />
                            </Link>
                        </div>

                        <div className="mt-6 px-6">
                            <SidebarProfile
                                name={displayName}
                                email={displayEmail}
                                initial={displayInitial}
                                photoUrl={displayPhoto}
                            />
                        </div>

                        <hr className="border-slate-200 mt-6" />
                        {/* Aura hidden temporarily — /aura-ai-assistant route still works directly */}
                        {/* <div className="mt-4 px-6">
                            {auraLink(onClose)}
                        </div> */}
                        <nav className="mt-4 space-y-2 px-6 text-sm">
                            {navItems.map((item) => (
                                <SidebarNavItem
                                    key={item.key}
                                    to={item.to}
                                    label={t(item.key, item.label)}
                                    activeLabel={activeLabel}
                                    onClick={onClose}
                                    badge={item.badge}
                                />
                            ))}
                            <button
                                type="button"
                                onClick={handleSignOut}
                                className="px-4 py-3 text-left text-sm font-semibold text-rose-400 focus:outline-none"
                            >
                                {t("SIDEBAR_SIGN_OUT")}
                            </button>
                        </nav>
                        <div className="flex-1" />
                        <div className="px-6 pb-6">
                            <SidebarLanguageSwitch value={language} onChange={handleLanguageChange} />
                        </div>
                    </aside>
                </div>
            )}
        </>
    );
};

export default Sidebar;
